import { API_BASE_URL } from '../constants/api';
import type { AuthResponse } from '../types/auth';
import * as Sentry from '@sentry/react';

// 204 No Content / empty-body responses don't survive response.json(); endpoints
// that return them (e.g. PUT /business-embed/exposed, DELETE /business-embed/tokens/{id})
// would otherwise throw "Unexpected end of JSON input". Returns undefined cast as T;
// callers that declared apiClient.put<void>(...) get the void they expected.
async function parseJsonOrEmpty<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.status === 205) {
    return undefined as unknown as T;
  }
  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return undefined as unknown as T;
  }
  const text = await response.text();
  if (text.length === 0) {
    return undefined as unknown as T;
  }
  return JSON.parse(text) as T;
}

export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(
    message: string,
    status: number,
    data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Direct localStorage access to avoid circular dependency with authService
const authStorage = {
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  },
  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  },
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  },
  setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  },
  setUser(user: string): void {
    localStorage.setItem('user', user);
  },
  clear(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
};

// Track if token refresh is in progress to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<AuthResponse> | null = null;

export const apiClient = {
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipAuthRetry = false
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const apiError = new ApiError(
          errorData?.message || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        );

        // Handle 401 Unauthorized errors with automatic token refresh
        if (response.status === 401 && !skipAuthRetry) {
          // If this is the refresh endpoint itself failing, clear auth and redirect
          if (endpoint.includes('/Auth/refresh')) {
            authStorage.clear();
            window.location.href = '/login';
            throw apiError;
          }

          try {
            // If already refreshing, wait for that refresh to complete
            if (isRefreshing && refreshPromise) {
              await refreshPromise;
            } else {
              // Start token refresh
              isRefreshing = true;

              const refreshToken = authStorage.getRefreshToken();
              if (!refreshToken) {
                // No refresh token available, clear auth and redirect
                authStorage.clear();
                window.location.href = '/login';
                throw apiError;
              }

              // Call refresh endpoint
              refreshPromise = this.request<AuthResponse>(
                '/Auth/refresh',
                {
                  method: 'POST',
                  body: JSON.stringify({ refreshToken }),
                },
                true // Skip auth retry for the refresh call itself
              );

              const authResponse = await refreshPromise;

              // Update stored tokens
              authStorage.setToken(authResponse.token);
              authStorage.setRefreshToken(authResponse.refreshToken);
              authStorage.setUser(JSON.stringify(authResponse.user));

              // Dispatch custom event to notify AuthContext of token update
              window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
                detail: authResponse
              }));
            }

            // Get the new token and retry the original request
            const newToken = authStorage.getToken();

            // Update authorization header if present
            const retryConfig = { ...config };
            const headers = new Headers(config.headers);

            if (newToken && (headers.has('Authorization') || options.headers && 'Authorization' in options.headers)) {
              headers.set('Authorization', `Bearer ${newToken}`);
              retryConfig.headers = headers;
            }

            const retryResponse = await fetch(url, retryConfig);

            if (!retryResponse.ok) {
              const retryErrorData = await retryResponse.json().catch(() => null);
              throw new ApiError(
                retryErrorData?.message || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
                retryResponse.status,
                retryErrorData
              );
            }

            return (await parseJsonOrEmpty<T>(retryResponse));
          } catch (refreshError) {
            // Token refresh failed, clear auth and redirect to login
            authStorage.clear();
            window.location.href = '/login';
            throw apiError;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        }

        throw apiError;
      }

      return (await parseJsonOrEmpty<T>(response));
    } catch (error) {
      // Capture error in Sentry with context
      Sentry.captureException(error, {
        tags: {
          endpoint,
          method: options.method || 'GET',
        },
      });

      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'An unknown error occurred',
        0
      );
    }
  },

  async get<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  async post<T>(endpoint: string, data?: unknown, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async put<T>(endpoint: string, data?: unknown, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async patch<T>(endpoint: string, data?: unknown, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async delete<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
};
